import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCreateAlanCredential } from "../../Query/useAlanCredentials";
import { CreateAlanCredentialPayload } from "../../Models/alanCredentials";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

const AlanCredentialCreatePage = () => {
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation("credentials");
  const { colors: workspace } = useWorkspaceColors();

  const { mutate: createCredential } = useCreateAlanCredential();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload: CreateAlanCredentialPayload = {
      name,
      client_id: clientId,
      client_secret: clientSecret,
    };

    createCredential(payload, {
      onSuccess: () => {
        setIsSubmitting(false);
        navigate("/alan_credentials");
      },
      onError: () => {
        setIsSubmitting(false);
      },
    });
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "720px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/alan_credentials")}
              size="small"
              sx={{ color: workspace.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>
            <VpnKeyOutlinedIcon
              sx={{ fontSize: 32, color: workspace.primary_dark }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: workspace.primary_dark }}
            >
              {t("title")}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ComoInput
                  label={t("name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <ComoInput
                  label={t("clientId")}
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  required
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <ComoInput
                  label={t("clientSecret")}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  required
                  disabled={isSubmitting}
                  type="password"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <ComoButton
                  type="submit"
                  fullWidth
                  disabled={
                    isSubmitting ||
                    !name.trim() ||
                    !clientId.trim() ||
                    !clientSecret.trim()
                  }
                  sx={{ py: 1.25, fontWeight: 600, mt: 1 }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("submit")
                  )}
                </ComoButton>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AlanCredentialCreatePage;
